import { Injectable } from "@angular/core"
import { FireStoreService } from "@shared/service/fire-store.service"
import { REFERENTIAL, ReferentialDTO, ReferentialService } from "@app/pages/referential/referential.service"
import { PersonService } from "@app/pages/person/person.service"

@Injectable({
  providedIn: "root",
})
export class UnitService extends FireStoreService<UnitDTO> {
  constructor(private refService: ReferentialService, private personService: PersonService) {
    super()
    this.init({
      collectionKey: "unitsV2",
      fullTextFields: ["name"],
      sorting: {
        field: "name",
      },
    })
  }

  toDTO(dto: any): Promise<UnitDTO> {
    return Promise.all([
      this.refService.getAllFromCache(REFERENTIAL.unity_type),
      this.refService.getAllFromCache(REFERENTIAL.unit_tags),
      this.personService.getAllFromCache().catch(() => []),
    ]).then(([refTypes, refTags, persons]) => {
      const ref = refTypes.find((x: ReferentialDTO) => x.id === dto.type?.id)
      dto.type_label = ref?.name || "?"
      dto.type_color = ref?.color || "?"
      dto.choreographer_label = persons.find((x) => x.id === dto.choreographer?.id)?.fullName || ""
      dto.tags_label = (dto.tags || [])
        .map((t: { id: string }) => refTags.find((x: ReferentialDTO) => x.id === t.id)?.name || "?")
        .join(", ")
      const version =
        (dto.versions || []).find((v: UnitVersionDTO) => v.id === dto.versionIdDefault) ||
        dto.versions?.[dto.versions.length - 1]
      dto.versions_count = dto.versions?.length || 0
      dto.duration_default = version?.duration || ""
      dto.parts_count = version?.parts?.length || 0
      return dto
    })
  }

  // garantit un id à chaque version et une version par défaut valide
  // (absente ou ne pointant plus sur une version existante => la plus récente)
  ensureDefaultVersion(unit: UnitDTO): UnitDTO {
    unit.versions = unit.versions || []
    unit.versions.forEach((v) => (v.id = v.id || this.uuid()))
    if (!unit.versions.some((v) => v.id === unit.versionIdDefault)) {
      unit.versionIdDefault = unit.versions[unit.versions.length - 1]?.id || ""
    }
    return unit
  }

  // ids des versions et des rôles attribués à l'enregistrement
  beforeSavingHook(dto: any): any {
    dto.versions?.forEach((version: UnitVersionDTO) => {
      version.id = version.id || this.uuid()
      version.roles?.forEach((role: UnitRoleDTO) => (role.id = role.id || this.uuid()))
    })
    return dto
  }
}

// rôles dans l'ordre enregistré de la version, puis ceux hors ordre dans leur ordre d'arrivée (ordre des places)
export function sortRolesByOrder<R extends { id: string; name: string }>(
  roles: Array<R>,
  order?: Array<string>
): Array<R> {
  const rank = (id: string) => {
    const index = order?.indexOf(id) ?? -1
    return index < 0 ? Number.MAX_SAFE_INTEGER : index
  }
  return [...roles].sort((a, b) => rank(a.id) - rank(b.id))
}

export interface UnitDTO {
  id: string
  name: string
  subtitle?: string
  choreographer?: { id: string /*personId*/ }
  type: { id: string /*REFERENTIAL.unity_type*/ }
  tags?: Array<{ id: string /*REFERENTIAL.unit_tags*/ }>
  versions: Array<UnitVersionDTO>
  versionIdDefault?: string // id of UnitVersionDTO loaded by default
  missing?: boolean // référencée par un programme mais introuvable en base (cf. ProgramService), non stocké

  type_label?: string
  type_color?: string
  tags_label?: string
  choreographer_label?: string
  versions_count?: number
  duration_default?: string
  parts_count?: number
  // units: UnitDTO
}

export interface UnitVersionDTO {
  id: string
  duration: string
  name: string
  roles?: Array<UnitRoleDTO> // legacy (lecture seule : plus créés ni édités côté UI)

  parts?: Array<{ id: string /*PartDTO*/ }>
  refPerformanceId?: string
  versionIdDefault?: string // id of PartDTO
  rolesOrder?: Array<string> // ids of RoleDTO
  sourceV1VersionId?: string // migration v1 → v2 : version de la pièce v1 (units) dont elle est issue
  otherSourceV1VersionIds?: Array<string> // migration v1 → v2 : autres versions v1 rattachées à cette version
}

export interface UnitRoleDTO {
  id: string
  name: string
  points: number
  group?: boolean
}
